import {
  Avatar,
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import Toast from "app/components/Toast";
import logo from "assets/icons/referee-main.svg";
import { Field, Form, Formik } from "formik";
import {
  selectForgotPasswordEmail,
  selectForgotPasswordOtp,
  setForgotPasswordEmail,
  setForgotPasswordOtp,
} from "lib/features/auth/authSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import authServices, { ResetPasswordDto } from "services/auth-service";
import { authStyles } from "styles/common/auth.styles";
import * as Yup from "yup";

const resetPasswordValidation = Yup.object({
  newPassword: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: Yup.string()
    .required("Confirm Password is required")
    .oneOf([Yup.ref("newPassword")], "Passwords must match"),
});

const ResetPassword = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const forgotPasswordEmail = useSelector(selectForgotPasswordEmail);
  const forgotPasswordOtp = useSelector(selectForgotPasswordOtp);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<boolean>(false);

  const initialValues: ResetPasswordDto = {
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  };

  const onSubmit = async (values: ResetPasswordDto) => {
    if (!forgotPasswordEmail || !forgotPasswordOtp) return;
    values.email = forgotPasswordEmail;
    values.otp = forgotPasswordOtp;
    try {
      setLoader(true);
      const result = await authServices.resetPassword(values);
      if (result.status === 201) {
        setOpen(true);
        setTimeout(() => {
          dispatch(setForgotPasswordOtp(undefined));
          dispatch(setForgotPasswordEmail(undefined));
          navigate("/login");
        }, 2000);
      }
    } catch (error: any) {
      setError(error?.response?.data?.message);
      setTimeout(() => {
        navigate("/forgot-password");
      }, 3000);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (!forgotPasswordEmail || !forgotPasswordOtp) {
      return navigate(-1);
    }
  }, []);

  return (
    <Box width="100%" position="relative">
      <Box sx={authStyles.authContainer(theme)}>
        <Box sx={authStyles.avatarContainer}>
          <Avatar sx={authStyles.avatar}>
            <img src={logo} alt="logo" style={authStyles.avatarImage} />
          </Avatar>
        </Box>

        <Box sx={authStyles.textContainer}>
          <Typography variant="h6" textAlign={"center"}>
            Reset Your Password
          </Typography>
          <Typography variant="body2" textAlign={"center"}>
            Please input a new password for your account. Make sure it’s at
            least 8 characters long.
          </Typography>
        </Box>

        <Formik
          initialValues={initialValues}
          validationSchema={resetPasswordValidation}
          onSubmit={(values) => onSubmit(values)}
        >
          {({ values, errors, touched, handleChange }) => (
            <Form>
              {/* New Password */}
              <Box>
                <Field
                  as={TextField}
                  name="newPassword"
                  label="New Password"
                  fullWidth
                  onChange={handleChange}
                  value={values.newPassword}
                  error={touched.newPassword && Boolean(errors.newPassword)}
                />
                {errors.newPassword && touched.newPassword && (
                  <Typography color="red" fontSize="12px">
                    {errors.newPassword}
                  </Typography>
                )}
              </Box>

              {/* Confirm Password */}
              <Box marginTop={2}>
                <Field
                  as={TextField}
                  name="confirmPassword"
                  label="Confirm Password"
                  fullWidth
                  onChange={handleChange}
                  value={values.confirmPassword}
                  error={touched.newPassword && Boolean(errors.newPassword)}
                />
                {errors.confirmPassword && touched.confirmPassword && (
                  <Typography color="red" fontSize="12px">
                    {errors.confirmPassword}
                  </Typography>
                )}
              </Box>

              {error && (
                <Typography sx={authStyles.errorText}>{error}</Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                sx={authStyles.actionBtn}
                disabled={loader}
              >
                Reset Password
              </Button>
            </Form>
          )}
        </Formik>
      </Box>

      <Toast
        open={open}
        handleClose={() => setOpen(false)}
        message="Password Reset Successfully"
      />
    </Box>
  );
};

export default ResetPassword;
